import type { Product } from "../interfaces/catalogProduct";
import { getPageLocaleUrl } from "./helpers";
import {
  buildCatalogProductDetailHref,
  resolveProductFromRouteParam,
} from "./productCatalog";
import {
  languageCodeFromActiveLanguage,
  languageCodesEqual,
  type LanguageLike,
} from "./languageCode";

type PageLike = {
  pageType?: string;
  languageCode?: string;
  url?: string;
};

type WebsiteLike = {
  useBrowserLanguage?: number;
};

export type ResolveNotFoundRedirectParams = {
  skipRedirect: boolean;
  isNotFoundContext: boolean;
  isBuilder: boolean;
  productsLoaded: boolean;
  catalogProductsLoaded: boolean;
  currentPathname: string;
  currentSearch: string;
  pages: PageLike[];
  pageLanguages: LanguageLike[];
  products: Product[];
  website: WebsiteLike;
  navigatorLanguages: readonly string[];
};

export function normalizePathname(value: string): string {
  const path = String(value || "").split("?")[0] || "/";
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
}

export function shouldRedirectPathname(
  currentPathname: string,
  targetPathname: string
): boolean {
  return (
    normalizePathname(targetPathname) !== normalizePathname(currentPathname)
  );
}

function jaccardSimilarity(s1: string, s2: string): number {
  const set1 = new Set(s1.split("/"));
  const set2 = new Set(s2.split("/"));
  const intersection = new Set([...set1].filter((x) => set2.has(x)));
  const union = new Set([...set1, ...set2]);
  return intersection.size / union.size;
}

function normalizeNavigatorLanguage(language: string): string {
  if (language.includes("-")) {
    return language.split("-")[0];
  }
  return language;
}

function isVisitorEnabled(
  language: LanguageLike & {
    visitor_state?: number;
    visitorState?: number;
  }
): boolean {
  return Number(language?.visitor_state ?? language?.visitorState ?? 0) === 1;
}

function resolveHomepageRedirect(
  pages: PageLike[],
  pageLanguages: LanguageLike[],
  website: WebsiteLike,
  navigatorLanguages: readonly string[]
): string | null {
  const firstLanguage = normalizeNavigatorLanguage(navigatorLanguages[0] ?? "");
  const activeLanguages = Array.isArray(pageLanguages)
    ? pageLanguages.filter((x) => isVisitorEnabled(x))
    : [];

  const homepages = pages
    .filter((page) => page.pageType === "frontPage")
    .filter((page) => {
      const matchingVisitorLanguages = activeLanguages.filter((activeLanguage) =>
        languageCodesEqual(
          page.languageCode,
          languageCodeFromActiveLanguage(activeLanguage)
        )
      );
      return matchingVisitorLanguages.length > 0;
    });

  if (!homepages.length) {
    return null;
  }

  let selectedHomePage: PageLike | undefined;

  if (website.useBrowserLanguage === 1) {
    const homepageFirstLanguage = homepages.filter((page) =>
      languageCodesEqual(page.languageCode, firstLanguage)
    );

    if (homepageFirstLanguage.length === 0 && navigatorLanguages.length >= 2) {
      const secondLanguage = normalizeNavigatorLanguage(
        navigatorLanguages[1] ?? ""
      );
      selectedHomePage = homepages.find((page) =>
        languageCodesEqual(page.languageCode, secondLanguage)
      );
    } else if (homepageFirstLanguage.length) {
      selectedHomePage = homepageFirstLanguage[0];
    }
  } else if (website.useBrowserLanguage === 0) {
    const firstVisitor = activeLanguages[0];
    const targetCode = languageCodeFromActiveLanguage(firstVisitor);
    if (targetCode) {
      selectedHomePage = homepages.find((page) =>
        languageCodesEqual(page.languageCode, targetCode)
      );
    }
  }

  if (!selectedHomePage) {
    selectedHomePage = homepages[0];
  }

  return selectedHomePage ? getPageLocaleUrl(selectedHomePage, pageLanguages) : null;
}

export function resolveHomepageRedirectUrl(params: {
  pages: PageLike[];
  pageLanguages: LanguageLike[];
  website: WebsiteLike;
  navigatorLanguages: readonly string[];
}): string | null {
  return resolveHomepageRedirect(
    params.pages,
    params.pageLanguages,
    params.website,
    params.navigatorLanguages
  );
}

function resolveClosestPageRedirect(
  currentPathname: string,
  currentSearch: string,
  pages: PageLike[],
  pageLanguages: LanguageLike[],
  products: Product[],
  website: WebsiteLike,
  navigatorLanguages: readonly string[],
  productCatalogReady: boolean
): string | null {
  let maxSimilarity = -1;
  let closestPage: PageLike | null = null;

  for (const page of pages) {
    if (page.pageType === "notFound") {
      continue;
    }

    const url = getPageLocaleUrl(page, pageLanguages);
    const similarity = jaccardSimilarity(currentPathname, url);
    if (similarity > maxSimilarity) {
      maxSimilarity = similarity;
      closestPage = page;
    }
  }

  if (!closestPage) {
    return null;
  }

  const matchedPage = closestPage;

  if (matchedPage.pageType === "product") {
    const pageurl = getPageLocaleUrl(matchedPage, pageLanguages).split(":")[0];
    const productParam =
      currentPathname.split("/")[currentPathname.split("/").length - 1];
    const resolved = resolveProductFromRouteParam(productParam, products);

    if (!resolved) {
      if (!productCatalogReady) {
        return null;
      }

      return resolveHomepageRedirect(
        pages,
        pageLanguages,
        website,
        navigatorLanguages
      );
    }

    const locale =
      matchedPage.languageCode ??
      languageCodeFromActiveLanguage(pageLanguages[0]) ??
      "et";
    return buildCatalogProductDetailHref(pageurl, resolved.parent, locale);
  }

  if (matchedPage.pageType === "products") {
    return `${getPageLocaleUrl(matchedPage, pageLanguages)}${currentSearch}`;
  }

  const navigateUrl = `${getPageLocaleUrl(matchedPage, pageLanguages)}`;
  const targetPath = normalizePathname(navigateUrl);
  const currentPath = normalizePathname(currentPathname);
  return targetPath === currentPath ? navigateUrl + currentSearch : navigateUrl;
}

/** Resolves the storefront redirect target when the active page is missing or notFound. */
export function resolveNotFoundPageRedirectUrl(
  params: ResolveNotFoundRedirectParams
): string | null {
  if (params.skipRedirect || !params.isNotFoundContext || params.isBuilder) {
    return null;
  }

  const { currentPathname, currentSearch } = params;

  if (currentPathname === "/" || currentPathname === "") {
    return resolveHomepageRedirect(
      params.pages,
      params.pageLanguages,
      params.website,
      params.navigatorLanguages
    );
  }

  if (currentPathname.includes("/notFoundProduct/")) {
    return resolveHomepageRedirect(
      params.pages,
      params.pageLanguages,
      params.website,
      params.navigatorLanguages
    );
  }

  return resolveClosestPageRedirect(
    currentPathname,
    currentSearch,
    params.pages,
    params.pageLanguages,
    params.products,
    params.website,
    params.navigatorLanguages,
    params.productsLoaded && params.catalogProductsLoaded
  );
}

export function shouldNavigateToRedirectTarget(
  redirectUrl: string | null,
  currentPathname: string,
  currentSearch: string
): boolean {
  if (!redirectUrl) {
    return false;
  }
  const current = `${currentPathname}${currentSearch}`;
  return current !== redirectUrl;
}

export function shouldNavigateToProductNotFound(params: {
  routeParam: string | undefined;
  product: unknown | null;
  productsLoaded: boolean;
  catalogProductsLoaded: boolean;
  isBuilder: boolean;
}): boolean {
  if (!params.routeParam || params.isBuilder) {
    return false;
  }

  return (
    params.productsLoaded &&
    params.catalogProductsLoaded &&
    !params.product
  );
}

